import { useEffect, useState, useCallback } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Settings,
  ExternalLink,
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import {
  OnePasswordVault,
  OnePasswordStatus,
  OnePasswordConfig,
  ApiResponse,
} from '../types';
import PageHeader from '../components/ui/PageHeader';
import { SkeletonCard } from '../components/ui/Skeleton';

export default function OnePasswordPage() {
  const { fetchApi } = useApi();
  const [status, setStatus] = useState<OnePasswordStatus | null>(null);
  const [config, setConfig] = useState<OnePasswordConfig | null>(null);
  const [vaults, setVaults] = useState<OnePasswordVault[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<OnePasswordStatus>>('/1password/status');
      if (res.data) setStatus(res.data);
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  }, [fetchApi]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<OnePasswordConfig>>('/1password/config');
      if (res.data) setConfig(res.data);
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  }, [fetchApi]);

  const loadVaults = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<OnePasswordVault[]>>('/1password/vaults');
      if (res.data) setVaults(res.data);
    } catch (error) {
      console.error('Failed to load vaults:', error);
    }
  }, [fetchApi]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      await Promise.all([loadStatus(), loadConfig()]);
      setLoading(false);
    }
    loadData();
  }, [loadStatus, loadConfig]);

  useEffect(() => {
    if (status?.connected) {
      loadVaults();
    }
  }, [status, loadVaults]);

  const handleToggleEnabled = async () => {
    setSaving(true);
    try {
      const res = await fetchApi<ApiResponse<OnePasswordConfig>>('/1password/config', {
        method: 'PUT',
        body: JSON.stringify({ enabled: !config?.enabled }),
      });
      if (res.data) setConfig(res.data);
    } catch (error) {
      console.error('Failed to update config:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl space-y-6">
        <PageHeader title="1Password Integration" />
        <SkeletonCard lines={4} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <PageHeader title="1Password Integration" />
      </div>

      {/* Connection Status Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-4">
            {status?.connected ? (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <AlertCircle className="h-6 w-6 text-yellow-600" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {status?.connected ? 'Connected' : 'Not Connected'}
              </h2>
              <p className="text-sm text-slate-500">{status?.message}</p>
            </div>
          </div>

          {status?.connected && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Accessible Vaults</p>
                  <p className="font-medium text-slate-900">{vaults.length}</p>
                </div>
                <div>
                  <p className="text-slate-500">Integration</p>
                  <p className="font-medium text-slate-900">
                    {config?.enabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!status?.connected && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <h3 className="text-sm font-medium text-slate-900 mb-2">Setup Instructions</h3>
              <p className="text-sm text-slate-600 mb-2">
                This integration requires a 1Password Connect server.
              </p>
              <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                <li>Deploy 1Password Connect server (see <a href="https://developer.1password.com/docs/connect/" className="text-brand-600 underline" target="_blank" rel="noopener noreferrer">docs</a>)</li>
                <li>Create credentials file and access token</li>
                <li>Add environment variables:</li>
              </ol>
              <pre className="mt-2 p-2 bg-slate-200 rounded text-xs overflow-x-auto">
{`ONEPASSWORD_CONNECT_HOST=https://your-connect-server.example.com
ONEPASSWORD_CONNECT_TOKEN=your_access_token`}
              </pre>
              <p className="text-sm text-slate-600 mt-2">Then restart the worker.</p>
            </div>
          )}
        </div>
      </div>

      {/* Settings Card */}
      {status?.connected && (
        <div className="card">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-400" />
              <h2 className="text-lg font-semibold text-slate-900">Settings</h2>
            </div>
          </div>
          <div className="p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Enable 1Password Integration</p>
                <p className="text-sm text-slate-500">
                  Allow fetching credentials from 1Password for registrar accounts
                </p>
              </div>
              <button
                onClick={handleToggleEnabled}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config?.enabled ? 'bg-brand-600' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Accessible Vaults */}
            <div>
              <p className="font-medium text-slate-900 mb-2">Accessible Vaults</p>
              <div className="space-y-2">
                {vaults.map((vault) => (
                  <div
                    key={vault.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{vault.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{vault.id}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Link to Registrars */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                To import registrar accounts from 1Password, go to{' '}
                <a href="/registrars" className="font-medium underline inline-flex items-center gap-1">
                  Registrar Accounts
                  <ExternalLink className="h-3 w-3" />
                </a>{' '}
                and use the "Scan Vaults" feature.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
